import { useCallback, useEffect, useState } from "react";
import { Command, Moon, Sun } from "lucide-react";

import { api } from "./api";
import { AuthScreen } from "./components/AuthScreen";
import { ChatView } from "./components/ChatView";
import { CommandPalette } from "./components/CommandPalette";
import { Modal } from "./components/Modal";
import { Sidebar } from "./components/Sidebar";
import { Toast } from "./components/Toast";
import { MemoryPage } from "./pages/MemoryPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { AuthStatus, Chat } from "./types";

type Page = "chat" | "memory" | "settings";

function App() {
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [page, setPage] = useState<Page>("chat");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">(() => (localStorage.getItem("supergpt-theme") === "dark" ? "dark" : "light"));
  const [renameChat, setRenameChat] = useState<Chat | null>(null);
  const [renameText, setRenameText] = useState("");
  const [deleteChat, setDeleteChat] = useState<Chat | null>(null);
  const [toast, setToast] = useState<{ message: string; kind: "success" | "error" } | null>(null);

  const showToast = useCallback((message: string, kind: "success" | "error" = "success") => {
    setToast({ message, kind });
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadChats = useCallback(async () => {
    const result = await api.chats();
    setChats(result);
    setActiveChatId((current) => current && result.some((chat) => chat.id === current) ? current : result[0]?.id ?? null);
  }, []);

  useEffect(() => { api.authStatus().then(setAuth).catch(() => setAuth({ setupRequired: false, setupTokenRequired: false, authenticated: false })); }, []);
  useEffect(() => { if (auth?.authenticated) void loadChats().catch((error) => showToast(error.message, "error")); }, [auth?.authenticated, loadChats, showToast]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.variant = "calm";
    localStorage.setItem("supergpt-theme", theme);
  }, [theme]);
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault(); setCommandOpen(true);
      }
    };
    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  }, []);

  const createChat = async () => {
    try {
      const chat = await api.createChat();
      await loadChats();
      setActiveChatId(chat.id); setPage("chat"); setDrawerOpen(false);
    } catch (error) { showToast(error instanceof Error ? error.message : "Не удалось создать чат", "error"); }
  };

  const saveRename = async () => {
    if (!renameChat || !renameText.trim()) return;
    try { await api.renameChat(renameChat.id, renameText.trim()); setRenameChat(null); await loadChats(); }
    catch (error) { showToast(error instanceof Error ? error.message : "Не удалось переименовать чат", "error"); }
  };

  const confirmDelete = async () => {
    if (!deleteChat) return;
    try {
      await api.deleteChat(deleteChat.id);
      setDeleteChat(null);
      await loadChats();
      showToast("Чат удалён");
    } catch (error) { showToast(error instanceof Error ? error.message : "Не удалось удалить чат", "error"); }
  };

  const logout = async () => {
    await api.logout().catch(() => undefined);
    setAuth({ setupRequired: false, setupTokenRequired: false, authenticated: false });
    setChats([]); setActiveChatId(null);
  };

  if (!auth) return <div className="app-loader"><span className="brand-mark large">S</span></div>;
  if (!auth.authenticated) return <AuthScreen setupRequired={auth.setupRequired} setupTokenRequired={auth.setupTokenRequired} onAuthenticated={() => setAuth({ setupRequired: false, setupTokenRequired: false, authenticated: true })} />;

  const goPage = (nextPage: Page) => { setPage(nextPage); setDrawerOpen(false); };

  return (
    <div className="app-shell">
      <Sidebar
        chats={chats}
        activeChatId={activeChatId}
        page={page}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNewChat={() => void createChat()}
        onSelectChat={(id) => { setActiveChatId(id); setPage("chat"); setDrawerOpen(false); }}
        onPage={goPage}
        onRename={(chat) => { setRenameChat(chat); setRenameText(chat.title); }}
        onDelete={setDeleteChat}
        onLogout={() => void logout()}
      />

      <div className="main-surface">
        <div className="global-actions">
          <button className="icon-button button-ghost command-button" type="button" aria-label="Открыть палитру команд" title="Команды" onClick={() => setCommandOpen(true)}><Command /></button>
          <button className="icon-button button-ghost" type="button" aria-label="Переключить тему" title="Переключить тему" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
            {theme === "dark" ? <Sun /> : <Moon />}
          </button>
        </div>
        {page === "chat" && <ChatView chatId={activeChatId} onOpenSidebar={() => setDrawerOpen(true)} onChatsChanged={() => void loadChats()} onSettings={() => goPage("settings")} onToast={showToast} />}
        {page === "memory" && <MemoryPage onOpenSidebar={() => setDrawerOpen(true)} onToast={showToast} />}
        {page === "settings" && <SettingsPage onOpenSidebar={() => setDrawerOpen(true)} onToast={showToast} />}
      </div>

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} onNewChat={() => void createChat()} onMemory={() => goPage("memory")} onSettings={() => goPage("settings")} />
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {renameChat && (
        <Modal title="Переименовать чат" onClose={() => setRenameChat(null)} actions={<><button className="button button-ghost" type="button" onClick={() => setRenameChat(null)}>Отмена</button><button className="button button-primary" type="button" onClick={() => void saveRename()}>Сохранить</button></>}>
          <input autoFocus value={renameText} maxLength={120} onChange={(event) => setRenameText(event.target.value)} onKeyDown={(event) => event.key === "Enter" && void saveRename()} />
        </Modal>
      )}
      {deleteChat && (
        <Modal title="Удалить чат?" onClose={() => setDeleteChat(null)} actions={<><button className="button button-ghost" type="button" onClick={() => setDeleteChat(null)}>Отмена</button><button className="button button-danger" type="button" onClick={() => void confirmDelete()}>Удалить</button></>}>
          <p>«{deleteChat.title}» и его файлы будут удалены без возможности восстановления.</p>
        </Modal>
      )}
    </div>
  );
}

export default App;
